/**
 * Sprint 76 — Los mensajes que hoy se teclean a mano.
 *
 * Todo el flujo a domicilio de Asados Las Espadas ocurre en un chat: el cliente escribe, la
 * dueña contesta, pregunta el envío en un grupo, dicta la cuenta bancaria, avisa que salió y
 * pregunta si llegó. Cada uno de esos mensajes se escribe a mano, con el pedido en pantalla y
 * los dedos en otro teclado — y ahí se equivocan los montos.
 *
 * Aquí se arman con lo que el pedido ya tiene. El operador sólo pega. **No hay integración con
 * WhatsApp**: eso exige la Cloud API de Meta (servidor con webhook público, verificación de
 * negocio y costo por conversación) y es una decisión aparte; las librerías no oficiales
 * quedaron descartadas porque el castigo por violar los ToS cae sobre el número del negocio.
 *
 * Módulo **puro** a propósito: sin Electron, sin Prisma, sin `Date.now()` implícito. Lo usan el
 * renderer del escritorio y el del track web, y se puede probar entero.
 */

import { centavosAPesos } from './centavos';
import { buildMapaUrl, esUrlDeCoordenadas } from './mapaUbicacion';

/** Formatea centavos como "$1,234.50" sin depender del locale del renderer. */
export function importe(centavos: number, simbolo = '$'): string {
  const pesos = centavosAPesos(Math.max(0, Math.round(centavos)));
  const [enteros, dec = '00'] = pesos.toFixed(2).split('.');
  const conMiles = enteros.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${simbolo}${conMiles}.${dec}`;
}

export interface RenglonPedido {
  cantidad: number;
  nombre: string;
}

export interface DatosPedidoMensaje {
  folio: number;
  clienteNombre?: string | null;
  items: RenglonPedido[];
  /** Total de la comida, en centavos (sin envío). */
  totalComida: number;
  /** Costo del envío en centavos. `null` = todavía no se cotiza. */
  costoEnvio?: number | null;
  /** true = se lo paga al repartidor al recibir; false = va incluido en lo que transfiere. */
  envioPagaCliente?: boolean;
  simboloMoneda?: string;
}

/** El saludo con el nombre, si lo hay. "Hola 👋" a secas si el cliente es nuevo y anónimo. */
function saludo(nombre?: string | null): string {
  const limpio = (nombre ?? '').trim();
  return limpio ? `Hola ${limpio} 👋` : 'Hola 👋';
}

/**
 * Confirmación de lo que se anotó, **antes** de saber el envío.
 *
 * Se manda en el momento en que el operador termina de capturar y todavía está esperando la
 * cotización del grupo. Dice el total de la comida y avisa explícitamente que el envío falta:
 * el error clásico es confirmar "son $335" y después tener que subirlo, que es como se pierde
 * un cliente.
 */
export function mensajeConfirmacionPedido(d: DatosPedidoMensaje): string {
  const s = d.simboloMoneda ?? '$';
  const lineas = d.items.map((i) => `• ${i.cantidad} ${i.nombre}`).join('\n');
  const partes = [`${saludo(d.clienteNombre)} Anoté:`, lineas, ''];

  if (d.costoEnvio == null) {
    partes.push(
      `Comida: *${importe(d.totalComida, s)}*`,
      'Ya estoy preguntando el costo del envío y te lo confirmo en un momento 🙂',
    );
  } else if (d.envioPagaCliente) {
    partes.push(
      `Comida: *${importe(d.totalComida, s)}*`,
      `Envío: *${importe(d.costoEnvio, s)}* — se los pagas al repartidor cuando llegue 🏍`,
    );
  } else {
    partes.push(
      `Comida: ${importe(d.totalComida, s)}`,
      `Envío: ${importe(d.costoEnvio, s)}`,
      `Total: *${importe(d.totalComida + d.costoEnvio, s)}*`,
    );
  }
  return partes.join('\n');
}

export interface DatosCotizacionGrupo {
  /** Folio del pedido. `0` o ausente cuando todavía no se guardó y no hay número que dar. */
  folio?: number | null;
  clienteLatitud?: number | null;
  clienteLongitud?: number | null;
  /** Referencia de zona **general** (colonia o fraccionamiento). Nunca la calle y el número. */
  zona?: string | null;
  /** Minutos estimados para que esté listo. */
  minutosListo?: number;
  nombreNegocio?: string | null;
}

/**
 * El mensaje que se copia al grupo "MOTO MANDADO" para pedir precio.
 *
 * **Regla de seguridad, no de estilo: aquí no va PII.** Ese grupo es un chat con gente que el
 * negocio no controla y del que no puede borrar nada; mandar en cada pedido "Marisol Jiménez,
 * 993 123 4567, Júpiter 206" es filtrar los datos personales de un tercero a un canal ajeno,
 * decenas de veces al día. Para **cotizar** basta el punto y cuánto falta para que esté listo.
 * El nombre, el teléfono y la dirección exacta van en la ficha impresa, que sólo lee quien
 * efectivamente entrega.
 *
 * Es la misma regla que el QR del despacho cumple desde el Sprint 8 (RN-S8-06), aplicada al
 * canal que faltaba. `mensajeCotizacionAlGrupoEsSeguro` es el guardián que lo comprueba.
 *
 * Si no hay coordenadas no se inventa un destino: se dice que se manda la ubicación aparte. Una
 * dirección escrita a mano no puede colarse por este camino.
 */
export function mensajeCotizacionAlGrupo(d: DatosCotizacionGrupo): string {
  const partes: string[] = [];
  const negocio = (d.nombreNegocio ?? '').trim();
  const zona = zonaPublicable(d.zona);
  partes.push(`📍 Servicio${zona ? ` a ${zona}` : ''}`);

  if (d.clienteLatitud != null && d.clienteLongitud != null) {
    partes.push(
      buildMapaUrl({ clienteLatitud: d.clienteLatitud, clienteLongitud: d.clienteLongitud }),
    );
  } else {
    partes.push('(te paso la ubicación en el siguiente mensaje)');
  }

  const folio = d.folio && d.folio > 0 ? ` — pedido ${d.folio}` : '';
  partes.push(`Recoger${negocio ? ` en ${negocio}` : ''}${folio}`);
  if (d.minutosListo && d.minutosListo > 0) {
    partes.push(`Listo en ~${d.minutosListo} min`);
  }
  partes.push('¿Costo y quién viene?');
  return partes.join('\n');
}

/**
 * La última defensa antes de que un texto salga al grupo: qué parte de la zona es publicable.
 *
 * Quien llama ya recorta la colonia de la dirección, pero eso es una decisión de pantalla y las
 * pantallas cambian. Esto corre **siempre**, en el mismo lugar donde se arma el mensaje, porque
 * una regla que sólo vive en la capa de arriba —o peor, sólo en los tests— es una regla que un
 * refactor apaga sin que nadie se entere.
 *
 * Descarta lo que delata un domicilio concreto: números de casa, enlaces, coordenadas y
 * direcciones con separadores. Lo que queda es un nombre de zona, que es lo único que el grupo
 * necesita para poner precio.
 */
export function zonaPublicable(zona?: string | null): string | undefined {
  const texto = (zona ?? '').trim();
  if (!texto) return undefined;
  if (/https?:\/\//i.test(texto)) return undefined;
  if (/@/.test(texto)) return undefined;
  // Con coma ya no es una zona: es una dirección con partes.
  if (/,/.test(texto)) return undefined;
  // Lo que se anuncia como calle, se descarta aunque venga sin número.
  if (/^(calle|av\.?|avenida|blvd\.?|boulevard|priv\.?|privada|and(ador)?|callej[oó]n|carretera|prolongaci[oó]n|retorno|circuito)\b/i.test(texto)) {
    return undefined;
  }
  /**
   * Un número **corto** al final es un número de casa ("Júpiter 206", "Universidad 41").
   *
   * Cuatro dígitos o más, no: en Villahermosa el fraccionamiento se llama literalmente **Tabasco
   * 2000**, y es una de las dos zonas que el negocio nombró al fijar su tarifa. Una regla que
   * borrara su nombre dejaría el mensaje al grupo sin la única pista útil, y el operador
   * volvería a escribirlo a mano — con la dirección completa, que es justo lo que se quiere
   * evitar.
   */
  if (/\s\d{1,3}\s*[-A-Za-z]?$/.test(texto)) return undefined;
  return texto.slice(0, 60);
}

/**
 * Guardián de la regla anterior, usable desde los tests y desde el propio código.
 *
 * Comprueba dos cosas sobre el mensaje que va a salir al grupo:
 *  1. Que no contenga ninguno de los datos personales del pedido (nombre, teléfono, dirección).
 *  2. Que el único enlace que lleva sea de **coordenadas** — un enlace corto de los que manda
 *     WhatsApp abre una ficha con la dirección completa, así que reenviarlo al grupo sería
 *     filtrar por la puerta de atrás justo lo que se quiso evitar.
 */
export function mensajeCotizacionAlGrupoEsSeguro(
  mensaje: string,
  pii: { nombre?: string | null; telefono?: string | null; direccion?: string | null },
): boolean {
  const texto = mensaje.toLowerCase();

  const nombre = (pii.nombre ?? '').trim().toLowerCase();
  if (nombre.length >= 3 && texto.includes(nombre)) return false;

  const telDigitos = (pii.telefono ?? '').replace(/\D/g, '');
  if (telDigitos.length >= 7) {
    const digitosDelMensaje = mensaje.replace(/\D/g, '');
    if (digitosDelMensaje.includes(telDigitos)) return false;
  }

  /**
   * De la dirección se comprueban sus palabras significativas y no la cadena completa: si el
   * operador escribió "Júpiter 206, Galaxia" y la zona es "Galaxia", la cadena completa no
   * aparece pero "júpiter" sí, y ése es el dato que sobra. Se ignoran las palabras cortas y
   * las genéricas para no convertir la comprobación en un "siempre falso".
   */
  const genericas = new Set([
    'calle', 'avenida', 'colonia', 'fraccionamiento', 'fracc', 'esquina', 'entre', 'del', 'las',
    'los', 'san', 'villahermosa', 'tabasco', 'sin', 'numero', 'número',
  ]);
  const palabras = (pii.direccion ?? '')
    .toLowerCase()
    .split(/[^a-záéíóúñ0-9]+/i)
    .filter((p) => p.length >= 4 && !genericas.has(p) && !/^\d+$/.test(p));
  const zonaEsPalabra = (p: string) => texto.includes(p);
  const sobra = palabras.filter(zonaEsPalabra);
  // La zona declarada sí puede aparecer: es lo único de la dirección que el grupo necesita.
  const zonaDeclarada = (mensaje.match(/^📍 Servicio a (.+)$/m)?.[1] ?? '').toLowerCase();
  const sobraDeVerdad = sobra.filter((p) => !zonaDeclarada.includes(p));
  if (sobraDeVerdad.length > 0) return false;

  const enlaces = mensaje.match(/https?:\/\/\S+/g) ?? [];
  return enlaces.every((u) => esUrlDeCoordenadas(u));
}

export interface DatosCobroMensaje {
  folio: number;
  clienteNombre?: string | null;
  /** Lo que hay que transferir AHORA, en centavos. */
  totalATransferir: number;
  /** Bloque libre con banco / titular / CLABE, tal como lo capturó el dueño en Ajustes. */
  datosDePago?: string | null;
  simboloMoneda?: string;
}

/**
 * Los datos para transferir.
 *
 * El folio va en el concepto porque es lo único que permite casar el dinero con el pedido
 * cuando llegan cuatro capturas seguidas y todas dicen el mismo monto. Sin eso, el cuadre del
 * día se hace a ojo.
 */
export function mensajeDatosParaPagar(d: DatosCobroMensaje): string {
  const s = d.simboloMoneda ?? '$';
  const partes = [
    `${saludo(d.clienteNombre)} Te confirmo tu pedido 🍗`,
    `Total a transferir: *${importe(d.totalATransferir, s)}*`,
  ];
  const datos = (d.datosDePago ?? '').trim();
  if (datos) {
    partes.push('', datos);
  }
  partes.push(
    '',
    `Concepto: *Pedido ${d.folio}*`,
    'Mándame la captura y en ese momento entra a cocina 🙌',
  );
  return partes.join('\n');
}

/** Recordatorio cuando pasó el tiempo de espera y no llega el comprobante. */
export function mensajeRecordatorioPago(d: {
  clienteNombre?: string | null;
  totalATransferir: number;
  simboloMoneda?: string;
}): string {
  const s = d.simboloMoneda ?? '$';
  return [
    `${saludo(d.clienteNombre)} ¿Todo bien con la transferencia?`,
    `Te aparto tu pedido un rato más — son ${importe(d.totalATransferir, s)}.`,
    'En cuanto me mandes la captura lo mando a cocina 🙂',
  ].join('\n');
}

/** Aviso de que el pedido salió, con lo que tiene que pagarle al repartidor si aplica. */
export function mensajeSalida(d: {
  clienteNombre?: string | null;
  nombreRepartidor?: string | null;
  costoEnvio?: number | null;
  envioPagaCliente?: boolean;
  simboloMoneda?: string;
}): string {
  const s = d.simboloMoneda ?? '$';
  const quien = (d.nombreRepartidor ?? '').trim();
  const partes = [`Ya salió tu pedido${quien ? ` con ${quien}` : ''} 🏍`];
  if (d.envioPagaCliente && d.costoEnvio && d.costoEnvio > 0) {
    partes.push(`Recuerda los *${importe(d.costoEnvio, s)}* del envío al recibirlo.`);
  }
  partes.push('¡Avísame cuando te llegue! 🙌');
  return partes.join('\n');
}

/** La pregunta que produce el acuse de recibo sin que nadie persiga a nadie. */
export function mensajeAcuse(d: { clienteNombre?: string | null }): string {
  return [
    `${saludo(d.clienteNombre)} ¿Ya te llegó tu pedido?`,
    'Si hubo algún detalle dime y lo resolvemos 🙏',
  ].join('\n');
}

/**
 * Sprint 95 — El mensaje con el que se le manda el menú a un cliente.
 *
 * Es el **paso 1 del flujo**, y hasta ahora no tenía dónde vivir: el tablero modela pedidos, y aquí
 * todavía no hay pedido. El cliente escribió "¿qué tienes?" y lo único que hace falta es contestar.
 *
 * Va sin nombre del cliente a propósito: se pega en cualquier chat sin editarlo, que es lo que
 * permite contestarle a diez personas en un minuto.
 */
export function mensajeMandarMenu(d: { enlace: string; nombreNegocio?: string | null }): string {
  const negocio = (d.nombreNegocio ?? '').trim();
  return [
    negocio ? `¡Hola! Bienvenido a ${negocio} 🍗` : '¡Hola! 🍗',
    '',
    'Aquí puedes ver todo lo que tenemos y armar tu pedido:',
    d.enlace,
    '',
    'Cuando lo mandes te escribimos con el total ya con el envío.',
  ].join('\n');
}

